import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ReactComponent as Logo } from '../logo.svg';
import { authApi } from '../services/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = (await authApi.login({ email, password })) as {
        access_token?: string;
        user?: { user_type?: string };
        message?: string;
        errors?: { email?: string[] };
      };
      if (res.access_token) {
        const isEmployee = res.user?.user_type === 'employee';
        navigate(isEmployee ? '/employee' : '/dashboard');
      } else {
        setError(res.message || res.errors?.email?.[0] || 'Login failed');
      }
    } catch {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: '∑', label: 'Accounting & Finance' },
    { icon: '↗', label: 'Sales & CRM' },
    { icon: '▦', label: 'Inventory' },
    { icon: '◷', label: 'HR & Payroll' },
  ];

  return (
    <div className="min-h-screen flex bg-white text-gray-900">
      {/* Left: Login form — one centered block, content left-aligned inside */}
      <motion.div
        className="w-full lg:w-[52%] flex flex-col justify-center items-center px-6 sm:px-10 lg:px-16 xl:px-24 py-12"
        initial={{ opacity: 0, x: -24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full max-w-[400px] flex flex-col items-start">
        <motion.div
          className="flex flex-col gap-4 mb-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 w-fit text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors group"
          >
            <span className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 group-hover:border-brand-300 group-hover:text-brand-600 group-hover:bg-brand-50/50 transition-colors shadow-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </span>
            Back to home
          </Link>
          <Link to="/" className="flex items-center gap-2 w-fit">
            <span className="w-9 h-9 rounded-lg bg-brand-600 flex items-center justify-center text-white shadow-lg shadow-brand-600/30">
              <Logo className="w-5 h-5 shrink-0" aria-hidden />
            </span>
            <span className="font-bold text-xl text-gray-900">SpynERP</span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
            Welcome back
          </h1>
          <p className="text-gray-600 mb-8">
            Sign in to your account to continue to SpynERP.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-5 w-full">
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none transition-all"
              autoComplete="email"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none transition-all"
              autoComplete="current-password"
            />
          </motion.div>
          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
              <span className="text-sm text-gray-600">Remember me</span>
            </label>
            <a href="#forgot" className="text-sm font-medium text-brand-600 hover:text-brand-700">
              Forgot password?
            </a>
          </motion.div>
          <motion.button
            type="submit"
            className={`w-full rounded-xl bg-brand-600 px-4 py-3.5 text-white font-semibold shadow-lg shadow-brand-600/25 hover:bg-brand-700 hover:shadow-brand-600/35 transition-all focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </motion.button>
        </form>

        <motion.p
          className="mt-8 text-sm text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Don&apos;t have an account?{' '}
          <Link to="/register" className="font-semibold text-brand-600 hover:text-brand-700">
            Start free trial →
          </Link>
        </motion.p>

        <motion.div
          className="mt-6 w-full rounded-xl border border-brand-200 bg-brand-50/50 p-4"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
        >
          <p className="text-sm font-semibold text-gray-800 mb-1">HR & Payroll</p>
          <p className="text-xs text-gray-600 mb-3">Employee time in / time out — sign in to clock in or out.</p>
          <Link
            to="/employee-login"
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 transition-colors"
          >
            <span className="w-5 h-5 rounded flex items-center justify-center bg-white/20">◷</span>
            Employee login
          </Link>
        </motion.div>
        </div>
      </motion.div>

      {/* Right: Info + animation */}
      <motion.div
        className="hidden lg:flex lg:w-[48%] relative overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-brand-800 items-center justify-center p-12 xl:p-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {/* Background glow */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.08) 0%, transparent 50%)',
          }}
        />

        <div className="relative z-10 max-w-md w-full">
          {/* Top card: slide in from right + fade */}
          <motion.div
            className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-8 shadow-xl mb-8"
            initial={{ opacity: 0, x: 48, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.h2
              className="text-2xl font-bold text-white mb-2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Run your business smarter
            </motion.h2>
            <motion.p
              className="text-brand-100 text-sm leading-relaxed mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
            >
              SpynERP unifies accounting, sales, inventory, HR, and operations in one platform.
            </motion.p>
            <div className="space-y-3">
              {features.map((f, i) => (
                <motion.div
                  key={f.label}
                  className="flex items-center gap-3 text-white/90"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + i * 0.1, type: 'spring', stiffness: 200 }}
                  whileHover={{ x: 4 }}
                >
                  <motion.span
                    className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-sm font-bold"
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.3)' }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    {f.icon}
                  </motion.span>
                  <span className="text-sm font-medium">{f.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Chart card: slide up + bars grow from bottom */}
          <motion.div
            className="rounded-2xl bg-navy-900/80 backdrop-blur border border-white/10 p-6 overflow-hidden"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.65 }}
            >
              Platform at a glance
            </motion.div>
            <div className="flex items-end gap-2 h-20">
              {[65, 85, 55, 90, 70, 78, 88].map((h, i) => (
                <motion.div
                  key={i}
                  className="flex-1 rounded-t bg-gradient-to-t from-brand-400 to-brand-500 min-h-[4px]"
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{
                    duration: 0.8,
                    delay: 0.7 + i * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  style={{ originY: 1 }}
                />
              ))}
            </div>
            <motion.div
              className="flex justify-between mt-3 text-xs text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <span>Last 7 days</span>
              <motion.span
                className="text-emerald-400 font-semibold"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.35, type: 'spring', stiffness: 300 }}
              >
                +23%
              </motion.span>
            </motion.div>
          </motion.div>

          {/* Floating badge: pop in + pulse dot */}
          <motion.div
            className="absolute -bottom-2 -right-4 rounded-xl bg-white text-gray-900 px-4 py-2 shadow-lg flex items-center gap-2"
            initial={{ opacity: 0, scale: 0.8, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 1.1, type: 'spring', stiffness: 260, damping: 20 }}
            whileHover={{ scale: 1.05, boxShadow: '0 10px 40px -10px rgba(0,0,0,0.25)' }}
          >
            <motion.span
              className="w-2 h-2 rounded-full bg-emerald-500"
              animate={{ scale: [1, 1.3, 1], opacity: [1, 0.8, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <span className="text-sm font-semibold">99.9% uptime SLA</span>
          </motion.div>
        </div>

        {/* Decorative orbs - breathing */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-white/10 blur-2xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-40 h-40 rounded-full bg-brand-400/20 blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>
    </div>
  );
}
